// src/pages/ConversationDetailPage.tsx

import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGetConversationDetailQuery,
  useAddMessageMutation,
} from '../api/conversationsApi';
import {
  useGetConversationActionsQuery,
  useExecuteAssistantMutation,
} from '../api/assistantApi';
import { useGetContactQuery } from '../api/contactsApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import ActionQueuePanel from '../components/conversations/ActionQueuePanel';
import ActiveCallShell from '../components/telephony/ActiveCallShell';
import { useTelephonyAvailability } from '../hooks/useTelephonyAvailability';
import { useTwilioVoiceDevice } from '../hooks/useTwilioVoiceDevice';

export default function ConversationDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [newMessage, setNewMessage] = useState('');
  const [callError, setCallError] = useState<string | null>(null);
  // Initialize assistant mode from URL param (e.g., ?assistant=true)
  const [assistantMode, setAssistantMode] = useState(
    searchParams.get('assistant') === 'true'
  );

  const {
    data: conversation,
    isLoading,
    error,
    refetch,
  } = useGetConversationDetailQuery(sessionId || '', {
    skip: !sessionId,
    pollingInterval: 5000, // Poll every 5 seconds for new messages
  });

  // Fetch actions separately using the new assistant API
  const { data: actions = [] } = useGetConversationActionsQuery(sessionId || '', {
    skip: !sessionId,
    pollingInterval: 3000, // Poll more frequently for action updates
  });

  const [addMessage, { isLoading: isSending }] = useAddMessageMutation();
  const [executeAssistant, { isLoading: isProcessing }] = useExecuteAssistantMutation();

  // Telephony availability check
  const {
    isTelephonyAvailable,
    canMakeCalls,
    reason: telephonyReason,
  } = useTelephonyAvailability();

  // The shared browser VoIP device (TelephonyProvider, mounted around every
  // authenticated route). Same call path as the /phone dialer and the
  // contact quick actions: Device.connect() via connectToNumber. The server
  // resolves the outbound from-number, so there is no from-number picker.
  const voice = useTwilioVoiceDevice();
  const callInProgress =
    voice.activeCall.kind === 'connecting' || voice.activeCall.kind === 'in_call';

  // Fetch full contact data if the conversation has a contactId
  const contactId = conversation?.session?.contactId;
  const { data: contact } = useGetContactQuery(contactId || '', {
    skip: !contactId,
  });

  // Check if we can show the call button
  const canShowCallButton = contact?.phone && isTelephonyAvailable && canMakeCalls;
  const showDisabledCallButton = contact?.phone && !isTelephonyAvailable;
  const canPlaceCall = !!canShowCallButton && voice.isReady && !callInProgress;

  // Get tooltip message for disabled call button
  const getCallButtonTooltip = () => {
    if (!contact?.phone) return 'Contact has no phone number';
    if (!isTelephonyAvailable) {
      switch (telephonyReason) {
        case 'no_integration':
        case 'no_phone_numbers':
          return 'Phone is not configured for this workspace. Configure Twilio under Settings → Integrations.';
        case 'integration_error':
          return 'Phone integration error. Check Settings → Integrations.';
        case 'no_permission':
          return 'You do not have permission to make calls.';
        default:
          return 'Phone is not available.';
      }
    }
    if (!canMakeCalls) return 'You do not have permission to make calls.';
    if (!voice.isReady) return 'Browser calling is not ready yet — open the Phone page to fix.';
    if (callInProgress) return 'A call is already in progress';
    return `Call ${contact.fullName}`;
  };

  // Place a real browser VoIP call. Nothing here reports success: the call's
  // own state (connecting / in call / failed) is shown by ActiveCallShell
  // below, driven by the device. Only a launch failure is reported.
  const handleCall = async () => {
    if (!canPlaceCall || !contact?.phone) return;
    setCallError(null);
    try {
      await voice.connectToNumber(contact.phone, {
        contactId: contact.id,
        contactName: contact.fullName,
      });
    } catch (err) {
      setCallError(err instanceof Error ? err.message : 'Failed to start call.');
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionId || !newMessage.trim()) return;

    try {
      if (assistantMode) {
        // Send message and process with assistant
        const result = await addMessage({
          sessionId,
          content: newMessage.trim(),
        }).unwrap();

        // Execute assistant to extract actions from the message
        if (result.message?.id) {
          await executeAssistant({
            conversationId: sessionId,
            messageId: result.message.id,
            autoExecute: true,
          }).unwrap();
        }
      } else {
        // Just send the message without assistant processing
        await addMessage({
          sessionId,
          content: newMessage.trim(),
        }).unwrap();
      }
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !conversation) {
    return <ErrorState message="Failed to load conversation." onRetry={refetch} />;
  }

  const { session, messages } = conversation;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <button
          onClick={() => navigate('/conversations')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="font-semibold text-gray-900 truncate">
            {session.title || 'Conversation'}
          </h1>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className={`px-2 py-0.5 font-medium rounded-full ${
              session.status === 'active' ? 'bg-green-100 text-green-700' :
              session.status === 'pending_human' ? 'bg-yellow-100 text-yellow-700' :
              session.status === 'human_active' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {session.status.replace('_', ' ')}
            </span>
            <span className="capitalize">{session.source} · {session.mode}</span>
            {contact && (
              <span className="text-gray-400">·</span>
            )}
            {contact && (
              <span className="text-gray-600">{contact.fullName}</span>
            )}
          </div>
        </div>

        {/* Call Button */}
        {(canShowCallButton || showDisabledCallButton) && (
          <button
            type="button"
            data-testid="conversation-call-button"
            onClick={handleCall}
            disabled={!canPlaceCall}
            title={getCallButtonTooltip()}
            aria-label={getCallButtonTooltip()}
            className={`p-2 rounded-lg transition-colors ${
              canPlaceCall
                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
          </button>
        )}
      </div>

      {callError && (
        <p role="alert" className="pt-2 text-sm text-red-600" data-testid="conversation-call-error">
          {callError}
        </p>
      )}

      {/* Action Queue Panel */}
      {actions.length > 0 && (
        <div className="py-3">
          <ActionQueuePanel actions={actions} isExpanded={false} />
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="p-4 bg-purple-50 rounded-xl inline-block mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <p className="text-gray-600 font-medium">Start the conversation</p>
            <p className="text-sm text-gray-500 mt-1">
              {assistantMode
                ? 'Ask LevelUp to create tasks, schedule events, or take actions'
                : 'Send a message to begin'}
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.senderType === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  message.senderType === 'user'
                    ? 'bg-blue-600 text-white'
                    : message.senderType === 'assistant'
                    ? 'bg-gray-100 text-gray-900'
                    : message.senderType === 'system'
                    ? 'bg-yellow-50 text-yellow-800 text-sm italic'
                    : 'bg-purple-50 text-purple-900'
                }`}
              >
                {message.senderType !== 'user' && message.senderType !== 'assistant' && (
                  <p className="text-xs font-medium mb-1 opacity-75 capitalize">
                    {message.senderType.replace('_', ' ')}
                  </p>
                )}
                <p className="whitespace-pre-wrap">{message.content}</p>
                <p className={`text-xs mt-1 ${
                  message.senderType === 'user' ? 'text-blue-200' : 'text-gray-400'
                }`}>
                  {new Date(message.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="pt-4 border-t border-gray-200">
        {/* Assistant Mode Toggle */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => setAssistantMode(!assistantMode)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              assistantMode
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <svg
              className={`w-4 h-4 ${assistantMode ? 'text-purple-600' : 'text-gray-500'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {assistantMode ? 'Assistant Mode ON' : 'Enable Assistant'}
          </button>
          {assistantMode && (
            <span className="text-xs text-gray-500">
              Actions will be extracted and executed automatically
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={
              assistantMode
                ? 'e.g., "Schedule a call with John tomorrow at 3pm"'
                : 'Type a message...'
            }
            className={`flex-1 px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              assistantMode ? 'border-purple-300 bg-purple-50' : 'border-gray-300'
            }`}
            disabled={session.status === 'closed'}
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || session.status === 'closed'}
            isLoading={isSending || isProcessing}
            className={assistantMode ? 'bg-purple-600 hover:bg-purple-700' : ''}
          >
            {assistantMode ? 'Send & Act' : 'Send'}
          </Button>
        </div>
        {session.status === 'closed' && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            This conversation has been closed.
          </p>
        )}
      </form>

      {/* Outbound call controls (inbound calls are owned by GlobalIncomingCallUI). */}
      {voice.activeCall.kind !== 'idle' && voice.activeCall.direction !== 'inbound' && (
        <ActiveCallShell
          activeCall={voice.activeCall}
          workspaceRecordingMode={
            voice.status.kind === 'ready' ? voice.status.recordingMode : undefined
          }
          onMute={voice.mute}
          onUnmute={voice.unmute}
          onEndCall={voice.endCall}
          onDismiss={voice.clearActiveCall}
          onSendDigit={voice.sendDigits}
          audioDevices={voice.audioDevices}
          onSelectInput={voice.setInputDevice}
          onSelectOutput={voice.setOutputDevice}
        />
      )}
    </div>
  );
}
